import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';

import EyePasswordImgSrc from '@/assets/icons/eye.svg';
import EyePasswordCrossedSrc from '@/assets/icons/eye-crossed.svg';

interface PasswordDialogProps {
  onCancel: () => void;
  onSubmit: (password: string) => void;
  open: boolean;
  message?: string;
  title?: string;
}

export const PasswordDialog: React.FC<PasswordDialogProps> = ({
  open,
  onSubmit,
  onCancel,
  title,
  message,
}) => {
  const [password, setPassword] = useState('');
  const [shouldHidePassword, setShouldHidePassword] = useState(true);
  const { t } = useTranslation();

  const handleSubmit = () => {
    onSubmit(password);
    setPassword('');
  };

  const handleCancel = () => {
    onCancel();
    setPassword('');
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title || t('public-view.enter_password')}</DialogTitle>
      <DialogContent>
        {message && <Box sx={{ marginBottom: 2 }}>{message}</Box>}
        <TextField
          autoFocus
          margin="dense"
          type={shouldHidePassword ? 'password' : 'text'}
          fullWidth
          variant="outlined"
          value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t('public-view.password_placeholder')}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgb(232, 240, 254) !important',
            },
            '& .MuiOutlinedInput-root:hover': {
              backgroundColor: 'rgb(232, 240, 254) !important',
            },
            '& .MuiOutlinedInput-root.Mui-focused': {
              backgroundColor: 'rgb(232, 240, 254) !important',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShouldHidePassword(!shouldHidePassword)} edge="end">
                  <img
                    src={shouldHidePassword ? EyePasswordImgSrc : EyePasswordCrossedSrc}
                    alt="Toggle password visibility"
                    style={{ height: '20px', width: '20px' }}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          {t('common.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
